////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: Persistent, route-keyed background layer (1.0.3.md §5)          //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////
//
//  Mounted in PublicLayout OUTSIDE <Outlet /> (fixed, full-viewport, behind
//  everything — z-index-negative, pointer-events-none) so it survives route
//  changes as one continuous layer while page content transitions above it.
//  AnimatePresence crossfades the OLD and NEW page's image for
//  DURATION.crossfadeBackground (600ms, lib/motion.ts) whenever the resolved
//  PageKey changes — a same-key navigation (e.g. one news article to another)
//  does not fade at all, by design; the background identity hasn't changed.
//
//  Placeholder era (1.0.3.md §0/§5): the images themselves are unmodified
//  `/scrape/**` photography (config.tsx's `cfg.assets.background`); the
//  "monochrome + theme tint + darken" treatment specified by the plan is applied
//  here as CSS (grayscale filter + a theme-aware tint overlay), NOT baked into the
//  source files.
//
//  1.0.3 POST-REVIEW TWEAK (11/08/2026, tweak #4): config.tsx's `BackgroundEntry.
//  treated` flag now drives whether that CSS treatment applies at all — a real,
//  pre-treated asset (Mike's `bgi-<page>-{light,dark}.webp` pair, `treated:
//  false`) renders as supplied, with only the theme-aware ink tint overlay (the
//  contrast mechanism PageHero's white-on-photo text assumes, see that file's
//  own MARK) — no grayscale/contrast/brightness filter piled on top of imagery
//  that is already monochrome. A `treated: true` entry (the remaining
//  `/scrape/**` placeholders) keeps the full CSS treatment exactly as before.
//
//  ROUND-2 (12/08/2026):
//    - Parallax is no longer Home-only — every route's background now gets the
//      same slow, transform-only drift (see PARALLAX_SPEED below), previously
//      scoped to `pageKey === 'home'`.
//    - The image plane is now taller than the viewport (IMAGE_HEIGHT_VH) and
//      the parallax translation is clamped to the resulting slack, so the
//      image's bottom edge can never rise above the viewport's bottom edge and
//      expose flat `bg-paper` behind it — the bug in
//      gui/debug/landing_background_movement.png (the dark smoke band had
//      scrolled up correctly, but the image itself ran out further down the
//      page). The clamp is a fixed budget derived from IMAGE_HEIGHT_VH, not the
//      actual page height (framer-motion's `useScroll()` here is windowed, not
//      target-scoped) — "clamp generously" rather than track exact page length.
//    - A second, UNIFORM veil (`VEIL_ALPHA`, `--color-paper`-tinted) now covers
//      every NON-home route, full-viewport, matching the layer's own `fixed`
//      positioning — i.e. it does not scroll as a band the way PageHero's old
//      per-hero scrim used to (that scrim is gone, see PageHero.tsx). Home
//      keeps its own hero-scoped smoke (HomeHero.tsx) instead, deferred by
//      Mike's own call — this veil is deliberately skipped for `pageKey ===
//      'home'` so the two treatments never stack. Theme-awareness comes for
//      free from `--color-paper` itself (white in light theme, near-black in
//      dark), matching PageHero's title text following `--color-ink` — see
//      test/contrast.hero-veil.test.ts for the numeric AA verification (both
//      themes, both `treated`/untreated imagery pipelines).
//
////////////////////////////////////////////////////////////////////////////////

import { useMemo } from 'react';
import { AnimatePresence, motion, useScroll, useTransform } from 'framer-motion';
import { useLocation } from 'react-router';

import { useConfig, type BackgroundPaths } from '@/config';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { crossfadeBackground, reduceVariants } from '@/lib/motion';

export type PageKey = keyof BackgroundPaths;

// MARK — route -> PageKey mapping, kept local to this component rather than
// derived from App.tsx's router table: App.tsx's route elements aren't labelled
// with a page identity today, and threading one through would touch the route
// table sibling agents are also working against this phase. `startsWith` prefix
// matching is deliberate so nested paths (`/project/:slug`, `/news/:slug`) fall
// under their listing page's background without a second entry each.
function resolvePageKey (pathname: string): PageKey {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/about')) return 'about';
  if (pathname.startsWith('/project')) return 'projects'; // /projects AND /project/:slug
  if (pathname.startsWith('/careers')) return 'careers';
  if (pathname.startsWith('/news')) return 'news'; // /news, /news/:slug, legacy /news-updates etc.
  if (pathname.startsWith('/contact')) return 'contact';
  if (pathname.startsWith('/privacy-cookies-terms-conditions')) return 'legal';
  // MARK — no dedicated background for 404/unrecognised paths; falling back to
  // 'home' keeps the layer always-populated rather than a flash of bare paper.
  // Flagging in case NotFound eventually wants its own page-key/asset instead.
  return 'home';
}

// MARK — dormant per §8: `bgv-home-{light,dark}.(webm|mp4)` does not exist as an
// asset yet. Left as a plain empty-string constant (not part of
// config.tsx's `cfg.assets`, which only ever holds paths to files that actually
// exist on disk) so the <video> branch below never renders and can never 404 or
// contend with LCP. Mike drops the real filename in here — the poster is
// `cfg.assets.background.home`, so a straight swap is all this needs.
const HOME_BACKGROUND_VIDEO_SRC: string = '';

// MARK — parallax (1.0.3.md §4: "slow parallax, transform-only, ~0.85 scroll
// factor"; round-2: extended to every route, not just Home). Added to this
// shared, foundation-owned component (rather than a second background element
// inside HomeHero.tsx) because §4's own wording — "the image as poster/
// fallback" for the eventual `bgv-` video — is describing THIS component's
// existing video-ready slot, not a duplicate one. `useScroll`/`useTransform`
// are framer-motion hooks already in the dependency tree (no new package) and
// are the transform-only mechanism the brief asks for — no layout-affecting
// property is read or written.
//
// speed=0.85 -> translateY(scrollY * (0.85 - 1)) = scrollY * -0.15: the image
// lags 15% behind a full 1:1 scroll, the standard "background moves slightly
// slower than content" parallax formula for a sub-1 speed factor.
const PARALLAX_SPEED = 0.85;

// The image plane's height, in vh, i.e. how much of it there is to reveal as
// the visitor scrolls before it runs out — MUST match the static `h-[140vh]`
// Tailwind class on the <img>/<video> below (same "asserted literal, not a
// computed string" convention as PageHero's old HERO_SPACER_VH/`min-h-[Nvh]`
// pairing: Tailwind v4 scans source text for class names, so an interpolated
// class built from this constant would silently never be generated).
const IMAGE_HEIGHT_VH = 140;

// The image's own slack beyond the viewport — how far it can be translated
// upward before its bottom edge lines up exactly with the viewport's bottom
// edge (translating any further would start exposing bg-paper below it, the
// "runs out to flat grey" bug in gui/debug/landing_background_movement.png).
// Expressed in vh so it scales with viewport height like the image itself;
// converted to px per-frame in the `useTransform` callback below.
const PARALLAX_SLACK_VH = IMAGE_HEIGHT_VH - 100;

// The uniform, full-page veil's opacity for every NON-home route (round-2) —
// see this file's header comment. 85% clears WCAG AA (4.5:1) for BOTH
// `text-ink` (page titles) and `text-muted` (subtitles, `.text-body-tertiary`)
// against BackgroundLayer's own treated/tinted imagery across the FULL 0-255
// source-grey range, in BOTH themes, for BOTH the CSS-monochrome placeholder
// pipeline (`treated: true`) and a real pre-treated asset (`treated: false`,
// e.g. /about's `bgi-about-*.webp`) — see test/contrast.hero-veil.test.ts,
// which recomputes this from the literal below rather than a hard-coded
// duplicate.
const VEIL_ALPHA_CLASS = 'bg-paper/60';

export function BackgroundLayer () {
  const { cfg } = useConfig();
  const { pathname } = useLocation();
  const reducedMotion = useReducedMotion();
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, (value) => {
    const slackPx = (typeof window === 'undefined' ? 0 : window.innerHeight) * (PARALLAX_SLACK_VH / 100);
    // `value * (speed - 1)` grows more negative (translates further up) without
    // bound as scroll increases — `Math.max` floors it at `-slackPx`, the exact
    // point the image's bottom edge reaches the viewport's bottom edge, so it
    // can never rise further and expose bg-paper beneath it.
    return Math.max(value * (PARALLAX_SPEED - 1), -slackPx);
  });

  const pageKey = useMemo(() => resolvePageKey(pathname), [pathname]);
  const variants = reduceVariants(crossfadeBackground, reducedMotion);
  const entry = cfg.assets.background[pageKey];
  const applyParallax = !reducedMotion;
  // Home keeps its own hero-scoped smoke (HomeHero.tsx) rather than this
  // uniform veil, per Mike's explicit "stays as-is, deferring further
  // judgement" — the two treatments are never stacked.
  const showUniformVeil = pageKey !== 'home';

  // Tweak #4: the grayscale+contrast+brightness stretch below exists ONLY to
  // fake a "monochrome architectural photo" out of an untouched colour scrape
  // photo (§0/§5's placeholder mandate) — a real, pre-treated `bgi-<page>-
  // {light,dark}.webp` pair (`entry.treated === false`) is already monochrome
  // and already the moody tone Mike wants, so re-running that stretch over it
  // would just double-darken/over-process supplied art. The ink/45 multiply
  // tint immediately below stays for BOTH cases regardless — it is the AA
  // text-contrast mechanism GlassTile's content sections rely on
  // (test/contrast.glass.test.ts), and is numerically sufficient on its own
  // (verified across the full 0-255 source-grey range in both themes) even
  // with no pre-treatment at all, so dropping the stretch for real assets
  // does not put any page's body copy below WCAG AA.
  const filterClass = entry.treated ? 'grayscale [filter:contrast(1.05)_brightness(0.75)]' : '';

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden bg-paper"
    >
      <AnimatePresence initial={false}>
        <motion.div
          key={pageKey}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="absolute inset-0"
        >
          {pageKey === 'home' && HOME_BACKGROUND_VIDEO_SRC ? (
            <motion.video
              className={`absolute inset-x-0 top-0 h-[140vh] w-full scale-105 object-cover ${filterClass}`}
              style={{ y: applyParallax ? parallaxY : 0 }}
              src={HOME_BACKGROUND_VIDEO_SRC}
              poster={entry.src}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
            />
          ) : (
            <motion.img
              src={entry.src}
              alt=""
              style={{ y: applyParallax ? parallaxY : 0 }}
              className={`absolute inset-x-0 top-0 h-[140vh] w-full scale-105 object-cover ${filterClass}`}
            />
          )}
          {/* Theme-aware tint overlay — the "monochrome + theme tint + slight
              darken" treatment (§5), and (tweak #4) the sole treatment real,
              pre-treated assets get. Reads the same palette tokens as
              everything else (styles/tokens.css via config.tsx), so it
              re-tints automatically on theme toggle without a second colour
              source. */}
          <div className="absolute inset-0 bg-ink/45 mix-blend-multiply" />

          {/* Round-2 uniform veil — every non-Home route, see this file's
              header comment. Deliberately flat (no gradient): "does NOT
              scroll as a band" per the brief, unlike Home's own hero-scoped
              smoke (HomeHero.tsx). */}
          {showUniformVeil && <div className={`absolute inset-0 ${VEIL_ALPHA_CLASS}`} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
